import { useEffect, useState } from "react";
import { Eye, EyeOff, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, apiError } from "../lib/api";
import { PageHeader } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { timeAgo } from "../lib/helpers";

const field = "w-full bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-slate-200";

function PasswordField({ label, value, onChange, autoComplete, count }) {
  const [visible, setVisible] = useState(false);
  return <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-slate-400">{label}</span>
    <div className="relative">
      <input type={visible ? "text" : "password"} value={value} onChange={onChange} autoComplete={autoComplete} className={`${field} pr-11`} />
      <button type="button" onClick={() => setVisible(!visible)} aria-label={visible ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200">
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
    {count && <span className={`mt-1 block text-[10px] ${value.length >= 12 ? "text-emerald-400" : "text-slate-500"}`}>{value.length}/12 minimum characters</span>}
  </label>;
}

export default function Account() {
  const { user, logout } = useAuth();
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm: "" });
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = () => API.get("/auth/sessions").then((r) => setSessions(r.data));
  useEffect(load, []);

  const changePassword = async (event) => {
    event.preventDefault();
    if (!passwords.current_password) return toast.error("Enter your current password");
    if (passwords.new_password.length < 12) return toast.error(`New password currently has ${passwords.new_password.length} characters; enter at least 12`);
    if (passwords.new_password !== passwords.confirm) return toast.error("New passwords do not match");
    setSaving(true);
    try {
      await API.post("/auth/change-password", { current_password: passwords.current_password, new_password: passwords.new_password });
      toast.success("Password changed. Sign in with your new password.");
      setTimeout(logout, 700);
    } catch (error) { toast.error(apiError(error)); }
    finally { setSaving(false); }
  };

  const revoke = async (id) => {
    try { await API.delete(`/auth/sessions/${id}`); toast.success("Session revoked"); load(); }
    catch (error) { toast.error(apiError(error)); }
  };

  return <div>
    <PageHeader title="My Account" subtitle={`${user.name} · ${user.email}`} />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form onSubmit={changePassword} className="mb-card p-5">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-4 w-4 text-emerald-400" /><span className="font-heading font-semibold text-slate-100">Change password</span></div>
        <div className="space-y-3">
          <PasswordField label="Current password" value={passwords.current_password} autoComplete="current-password" onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} />
          <PasswordField label="New password" value={passwords.new_password} autoComplete="new-password" count onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} />
          <PasswordField label="Confirm new password" value={passwords.confirm} autoComplete="new-password" count onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} />
          <button type="submit" disabled={saving} className="btn-gold px-4 py-2 text-sm disabled:opacity-50">{saving ? "Updating…" : "Update password"}</button>
        </div>
      </form>
      <div className="mb-card p-5">
        <div className="font-heading font-semibold text-slate-100 mb-4">Active sessions</div>
        <div className="space-y-2">{sessions.length ? sessions.map((session) => <div key={session.id} className="flex items-center justify-between gap-3 bg-slate-900/50 border border-slate-800 rounded-lg p-3">
          <div className="min-w-0"><div className="text-xs text-slate-300 truncate">{session.user_agent || "Unknown device"}</div><div className="text-[10px] text-slate-500">{session.ip || "Unknown IP"} · {timeAgo(session.created_at)}</div></div>
          <button onClick={() => revoke(session.id)} title="Revoke session" className="text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
        </div>) : <p className="text-sm text-slate-500">No active sessions.</p>}</div>
      </div>
    </div>
  </div>;
}
